import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "../components/Brand";
import { useScreenInit } from "../useScreenInit";
import { authClient } from "../auth-client";
import { useSession } from "../session";
import { clearAdminTenant } from "../tenant-context";

export function AdminLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const seed = useScreenInit();
  const [email, setEmail] = useState(seed.email !== undefined ? (seed.email as string) : "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState((seed.error as string) ?? "");
  const [working, setWorking] = useState(false);
  const sessionExpired =
    searchParams.get("reason") === "session-expired" || seed.reason === "session-expired";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim().toLowerCase().endsWith("@fmc.co.uk")) {
      setError("Admin access is limited to fmc.co.uk accounts.");
      return;
    }
    if (!password) {
      setError("Enter your password.");
      return;
    }
    setWorking(true);
    setError("");
    try {
      const result = await authClient.signIn.email({ email: email.trim(), password });
      if (result.error) {
        setError("The email address or password is incorrect.");
        return;
      }

      clearAdminTenant();
      const refreshed = await session.refresh();
      if (!refreshed) {
        setError(
          "You were signed in, but your admin session could not be loaded. Please try again.",
        );
        return;
      }

      navigate("/admin/", {
        replace: true,
        state: { notice: "Signed in successfully." },
      });
    } catch {
      setError("We couldn't sign you in. Check your connection and try again.");
    } finally {
      setWorking(false);
    }
  };

  const inputCls =
    "w-full h-11 px-3.5 rounded-xs border bg-white/[0.06] text-body text-white placeholder:text-white/35 focus:outline-none transition-colors";

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center px-6">
      <div className="w-full max-w-[400px]">
        <div className="flex justify-center mb-8">
          <div className="bg-surface-white rounded-md px-7 py-5">
            <Brand wordmark="Admin Console" align="center" logoHeight={54} />
          </div>
        </div>

        <form
          onSubmit={submit}
          className="rounded-xl border border-white/12 bg-white/[0.04] px-7 py-7 flex flex-col gap-4"
        >
          <div>
            <h1 className="text-title-md font-bold text-white">Sign in</h1>
            <p className="text-caption text-white/55 mt-1">
              Manage brand tenants, webhooks and platform settings.
            </p>
          </div>

          {sessionExpired && (
            <div
              role="status"
              className="flex items-start gap-2.5 rounded-md border border-white/15 bg-white/[0.07] px-3.5 py-3"
            >
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-red/20 text-status-red">
                <i className="fa-solid fa-clock-rotate-left text-[11px]" aria-hidden="true" />
              </span>
              <div>
                <p className="text-caption font-semibold text-white">Your session has expired</p>
                <p className="mt-0.5 text-caption text-white/60">
                  For your security, you have been signed out. Sign in again to continue.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-md border border-status-red/40 bg-[rgba(255,101,80,0.12)] px-3.5 py-2.5"
            >
              <i className="fa-solid fa-circle-exclamation text-[13px] text-status-red mt-0.5" />
              <span className="text-caption text-white/85">{error}</span>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="admin-email" className="text-caption font-medium text-white/75">
              FMC email
            </label>
            <input
              id="admin-email"
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputCls} ${error ? "border-status-red/60" : "border-white/15 focus:border-white/40"}`}
              placeholder="you@fmc.co.uk"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="admin-password" className="text-caption font-medium text-white/75">
              Password
            </label>
            <input
              id="admin-password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputCls} border-white/15 focus:border-white/40`}
              placeholder="••••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={working}
            aria-busy={working}
            className="h-11 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors mt-1"
          >
            {working ? "Signing in…" : "Sign in to Admin"}
          </button>

          <div className="flex items-start gap-2 pt-1">
            <i className="fa-solid fa-shield-halved text-[12px] text-white/45 mt-0.5" />
            <p className="text-micro text-white/45">
              Every admin session and client-management action is tenant-scoped and audit-logged.
            </p>
          </div>
        </form>

        <button
          onClick={() => navigate("/login")}
          className="w-full text-center text-caption text-white/50 hover:text-white/80 transition-colors mt-5"
        >
          Looking for the client sign in?
        </button>
      </div>
    </div>
  );
}
